import { Trophy, Crown, Medal } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import useLiveLeaderboard from "@/hooks/useLiveLeaderboard";
import { UpdatedAgo } from "@/components/LiveTop5Board";

const RANK_COLORS = ["#FFD700", "#C0C0C0", "#CD7F32"];

/**
 * Field standings. Pass `data` when the page already polls the leaderboard,
 * otherwise the component polls on its own.
 */
export default function LiveLeaderboard({ contestId, data: passed, refreshMs = 15000, testId = "live-leaderboard" }) {
  const { user } = useAuth();
  const own = useLiveLeaderboard(contestId, { refreshMs, enabled: !passed });
  const data = passed || own.data;
  const error = passed ? null : own.error;

  if (error) return <div className="text-white/40 text-sm">{error}</div>;
  if (!data) return <div className="text-white/40 text-sm animate-pulse">Loading leaderboard...</div>;

  const entries = data.entries || [];
  const status = data.contest?.status;
  const isSettled = status === "settled";
  const isLocked = status === "locked";
  const live = data.live || {};

  return (
    <div data-testid={testId} className="border border-white/10 bg-[#141414]">
      <div className="border-b border-white/10 p-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-white/40 text-xs uppercase tracking-widest mb-0.5">
            {isSettled ? "Final Standings" : isLocked ? "Live Standings" : "Current Field"}
          </div>
          <div className="font-display text-xl inline-flex items-center gap-2">
            {isLocked && <span className="w-1.5 h-1.5 bg-[#00FF66] rounded-full animate-pulse" />}
            {entries.length} PLAYERS
          </div>
          {isLocked && live.updated_at && (
            <div className="mt-0.5">
              <UpdatedAgo at={live.updated_at} testId="standings-updated-ago" />
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-white/40 text-xs uppercase tracking-widest">Prize</div>
          <div className="font-display text-xl text-white/40">NONE</div>
        </div>
      </div>
      {entries.length === 0 ? (
        <div className="p-8 text-center text-white/40 text-sm">No entries yet. Be the first!</div>
      ) : (
        <>
          {isLocked && entries.length < 2 && (data.contest?.pot || 0) > 0 && (
            <div className="px-4 py-2 border-b border-[#FF9F0A]/30 bg-[#FF9F0A]/10 text-[#FF9F0A] text-[10px] uppercase tracking-widest"
              data-testid="needs-more-players-note">
              Needs 2+ players to pay out — entry fees are refunded if it stays this small
            </div>
          )}
          <div className="divide-y divide-white/10 max-h-[560px] overflow-y-auto">
          {entries.map((e, i) => {
            const isYou = user && e.user_id === user.user_id;
            const rank = e.rank || i + 1;
            const rankColor = RANK_COLORS[rank - 1] || "#71717A";
            const Icon = rank === 1 ? Crown : rank <= 3 ? Medal : Trophy;
            const money = isSettled ? e.payout : e.projected_payout;
            return (
              <div
                key={e.entry_id}
                data-testid={`leaderboard-row-${rank}`}
                className={`flex items-center gap-3 p-3 ${isYou ? "bg-[#00FF66]/5" : ""}`}
              >
                <div
                  className="w-9 h-9 rounded-sm border flex items-center justify-center font-display text-lg flex-shrink-0"
                  style={{ borderColor: rankColor, color: rankColor }}
                >
                  {rank <= 3 ? <Icon size={16} /> : rank}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate flex items-center gap-2">
                    {e.user_name}
                    {isYou && (
                      <span className="text-[10px] uppercase tracking-widest text-[#00FF66] border border-[#00FF66]/40 px-1.5 py-0.5 rounded-sm">
                        You
                      </span>
                    )}
                  </div>
                  <div className="text-white/40 text-xs uppercase tracking-widest">
                    Score {e.score || 0}
                    {isLocked && typeof e.hits === "number" && (
                      <span className="ml-2 text-white/30">{e.hits}/5 in top 5</span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  {money > 0 ? (
                    <>
                      <div className="text-white/40 text-xs uppercase tracking-widest">
                        {isSettled ? "Payout" : "Projected"}
                      </div>
                      <div className="font-display text-lg text-[#00FF66]">+${money.toFixed(2)}</div>
                    </>
                  ) : (
                    <div className="font-display text-lg text-white/30">—</div>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </>
      )}
    </div>
  );
}
